import { useCallback, useRef, useState } from "react";
import { ApiError, search } from "../api/client";
import type { HopTierId, SearchResponse } from "../api/types";
import { buildZeroSearchCommand, type ZeroCommand } from "../lib/zeroCommands";

export type SearchState = {
  loading: boolean;
  result: SearchResponse | null;
  error: string | null;
  paymentCommand: ZeroCommand | null;
  lastQuery: string;
  lastTier: HopTierId;
};

export const useSearch = (initialTier: HopTierId = "hop1") => {
  const [state, setState] = useState<SearchState>({
    loading: false,
    result: null,
    error: null,
    paymentCommand: null,
    lastQuery: "",
    lastTier: initialTier,
  });
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(async (query: string, tier: HopTierId) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setState((s) => ({
      ...s,
      loading: true,
      error: null,
      paymentCommand: null,
      lastQuery: query,
      lastTier: tier,
    }));

    try {
      const result = await search(tier, { query }, ctrl.signal);
      if (ctrl.signal.aborted) return;
      setState({
        loading: false,
        result,
        error: null,
        paymentCommand: null,
        lastQuery: query,
        lastTier: tier,
      });
    } catch (e) {
      if (ctrl.signal.aborted) return;
      if (e instanceof ApiError && e.status === 402) {
        setState((s) => ({
          ...s,
          loading: false,
          error: "payment required",
          paymentCommand: buildZeroSearchCommand(tier, query),
        }));
        return;
      }
      const message =
        e instanceof ApiError
          ? `${e.status} ${e.message}`
          : e instanceof Error
            ? e.message
            : "search failed";
      setState((s) => ({ ...s, loading: false, error: message, paymentCommand: null }));
    }
  }, []);

  return { ...state, run };
};
